/* eslint-disable require-await */
import removeTrailingSlash from 'remove-trailing-slash';

export type HttpError = Error & {
  status?: number;
  error_code?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  details?: any;
};

export interface RequestParams {
  apiHostname: string;
  method?: string;
  endpoint: string;
  token?: string;
  raw?: boolean;
  headers?: Record<string, string>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  body?: any;
}

/**
 * Note: This method should not be called directly outside of the api client libraries.
 * Instead, you should use the methods provided by the api client libraries.
 */
export const apiRequest = async <T>(options: RequestParams): Promise<T> => {
  const {
    apiHostname,
    method = 'GET',
    raw,
    endpoint,
    token,
    body,
    headers = {}
  } = options;

  // Ensure the endpoint starts with `/`.
  // This allows us to easily join URLs when `$API_SERVER_URL` is set.
  /* istanbul ignore if */
  if (process.env.NODE_ENV === 'development') {
    if (endpoint[0] !== '/') {
      throw new Error('Endpoint must begin with "/"');
    }
  }

  if (!headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }

  if (!headers.Accept) {
    headers.Accept = 'application/json';
  }

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const host = removeTrailingSlash(apiHostname);
  const res = await fetch(`${host}${endpoint}`, {
    method,
    headers,
    body: raw ? body : JSON.stringify(body),
    // We need to include cookies in all requests (for feature flags).
    credentials: 'include'
  });

  if (raw) {
    if (res.ok) {
      return res as unknown as T;
    } else {
      throw new Error(`unexpected server error ${res.status}`);
    }
  }

  const type = res.headers.get('Content-Type');
  if (!type || !type.includes('json')) {
    // For whatever reason, the API didn't return JSON. We don't know what to do here, so log (and throw) an error.
    /* istanbul ignore next */
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.warn('API (%s) returned non-JSON response:', endpoint, res);
    }

    throw new Error('Unexpected result from API');
  }

  const responseBody = await res.json();

  if (res.ok) {
    return responseBody;
  }

  let errorMessage: string;
  if (responseBody.validationErrors) {
    // The signup API (POST /api/users) returns `.validationErrors` in addition to `.error`.
    // Take the first validation error.
    // TODO: use something like npmjs.org/aggregate-error and display all validation errors.
    errorMessage = Object.values(responseBody.validationErrors)[0] as string;
  } else if (responseBody.error) {
    // The API error handler returns the shape `{ error: string }`.
    errorMessage = responseBody.error;
  } else {
    // Throw a generic error if there was an issue with the error handler.
    errorMessage = 'Unexpected server error';
  }

  const error = new Error(errorMessage);
  (error as HttpError).status = res.status;
  (error as HttpError).error_code = responseBody.error_code;
  (error as HttpError).details = responseBody.details;

  throw error;
};
